// src/cockpit4/components/CEODecisionPanel.tsx
// ============================================
// CEO 决策面板（P0-功能2）- v2.0 详细改进计划
//
// 设计：基于全局数据主动给出经营建议
//   1. 实时 AI 建议卡片（3-5 条，可滚动展示）
//   2. 每条建议：问题描述 + 数据依据 + 建议方案 + 优先级
//   3. 按优先级/模块筛选
//   4. 一键执行：建议可直接转化为任务派发给对应模块
//   5. 建议历史记录（最近 7 天）
//
// 数据来源：
//   - EnterpriseBrain.get_decisions / get_pending_decisions
//   - 内置规则引擎：收入下降/内容产出下降/客户跟进不及时/任务延期等
//   - useDashboardData 的 CEO KPI
//
// 持久化：11_Strategy/AI建议/{date}.md
// ============================================

import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Brain,
  Sparkles,
  Loader2,
  AlertTriangle,
  TrendingDown,
  Users,
  FileText,
  CheckSquare,
  Filter,
  History,
  ChevronRight,
  RefreshCw,
  Zap,
  CheckCircle2,
  Clock,
  X,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { useDashboardData } from "../../hooks/useDashboardData";
import { useCockpit4Store } from "../store";

// ============================================
// 类型定义
// ============================================

/** 建议优先级 */
type Priority = "high" | "medium" | "low";

/** 建议模块 */
type SuggestionModule =
  | "finance"
  | "crm"
  | "content"
  | "task"
  | "risk"
  | "knowledge"
  | "strategy"
  | "system";

/** 决策建议项 */
interface DecisionSuggestion {
  /** 唯一 ID */
  id: string;
  /** 标题 */
  title: string;
  /** 问题描述 */
  problem: string;
  /** 数据依据 */
  evidence: string;
  /** 建议方案 */
  action: string;
  /** 优先级 */
  priority: Priority;
  /** 模块 */
  module: SuggestionModule;
  /** 来源（规则名/LLM/EnterpriseBrain） */
  source: string;
  /** 预期效果 */
  expectedEffect?: string;
  /** 生成时间 */
  createdAt: string;
  /** 是否已执行 */
  executed?: boolean;
  /** 执行时间 */
  executedAt?: string;
  /** V1.5 P0-2：涉及金额（元），用于审批阈值判断 */
  amount?: number;
  /** V1.5 P0-2：自动执行阈值（元），<=threshold 可自动执行，>threshold 必须人工批准 */
  autoThreshold?: number;
  /** V1.5 P0-2：决策状态（待定 / 已批准 / 已拒绝） */
  decision?: "proposed" | "approved" | "rejected";
  /** V1.5 P0-2：批准/拒绝时间 */
  decidedAt?: string;
  /** V1.5 P0-2：进一步分析展开 */
  expanded?: boolean;
}

// ============================================
// 规则引擎（内置 10+ 条核心规则）
// ============================================

interface RuleContext {
  ceoData: any;
  kpis: any[];
  risks: any[];
  decisions: any[];
  /** V1.5 P0-4：产品毛利归因（Profit Engine 提供，用于加投/止损决策） */
  skuMargins?: Array<{
    productName: string;
    revenue: number;
    marginRate: number | null;
    status: string;
    recommendation: string;
  }>;
}

interface Rule {
  id: string;
  name: string;
  module: SuggestionModule;
  priority: Priority;
  /** 规则触发判断 */
  check: (ctx: RuleContext) => { triggered: boolean; problem?: string; evidence?: string; action?: string; expectedEffect?: string };
}

const RULES: Rule[] = [
  {
    id: "revenue_drop_20",
    name: "收入下降预警",
    module: "finance",
    priority: "high",
    check: (ctx) => {
      const revenue = ctx.ceoData?.kpi?.revenue || 0;
      if (revenue > 0 && revenue < 1000) {
        return {
          triggered: true,
          problem: `今日收入 ${revenue} 元，明显低于预期水平`,
          evidence: `CEO Dashboard KPI: revenue=${revenue}（低于阈值 1000 元）`,
          action: "前往财务中心查看收入来源明细，定位下降原因（客户流失/单价下降/订单减少）",
          expectedEffect: "定位收入瓶颈，制定挽回方案",
        };
      }
      return { triggered: false };
    },
  },
  {
    id: "customer_zero",
    name: "新增客户为零预警",
    module: "crm",
    priority: "high",
    check: (ctx) => {
      const newCustomers = ctx.ceoData?.kpi?.newCustomers || 0;
      if (newCustomers === 0 && !ctx.ceoData?.degraded) {
        return {
          triggered: true,
          problem: "今日无新增客户，可能存在线索获取/转化问题",
          evidence: `CEO Dashboard KPI: newCustomers=0`,
          action: "前往客户中心检查销售漏斗，确认线索是否充足，跟进是否及时",
          expectedEffect: "恢复客户获取节奏",
        };
      }
      return { triggered: false };
    },
  },
  {
    id: "content_zero",
    name: "内容产出为零预警",
    module: "content",
    priority: "medium",
    check: (ctx) => {
      // 通过 KnowledgeRuntime 资产统计判断今日内容数
      // 此处仅检查降级情况
      if (ctx.ceoData?.degraded) {
        return {
          triggered: true,
          problem: "内容数据降级，可能 KnowledgeRuntime 未就绪",
          evidence: "CEO Dashboard degraded=true",
          action: "检查 KnowledgeRuntime 是否初始化，或前往媒体工作室手动生成内容",
          expectedEffect: "恢复内容产出统计",
        };
      }
      return { triggered: false };
    },
  },
  {
    id: "high_severity_risk",
    name: "高风险项预警",
    module: "risk",
    priority: "high",
    check: (ctx) => {
      const highRisks = ctx.risks.filter(
        (r) => (r.severity === "high" || r.severity === "critical") && r.status !== "mitigated"
      );
      if (highRisks.length > 0) {
        return {
          triggered: true,
          problem: `检测到 ${highRisks.length} 项高风险项未缓解`,
          evidence: highRisks
            .slice(0, 3)
            .map((r) => `「${r.title || "未命名"}」(${r.severity})`)
            .join("、"),
          action: "前往风险监控查看详情，制定缓解措施并跟踪执行",
          expectedEffect: "降低企业风险敞口",
        };
      }
      return { triggered: false };
    },
  },
  {
    id: "pending_decision",
    name: "待审批决策提醒",
    module: "strategy",
    priority: "medium",
    check: (ctx) => {
      const pending = ctx.decisions.filter((d) => d.status === "proposed");
      if (pending.length > 0) {
        return {
          triggered: true,
          problem: `有 ${pending.length} 项决策待审批`,
          evidence: pending
            .slice(0, 3)
            .map((d) => `「${d.title || "未命名决策"}」`)
            .join("、"),
          action: "前往战略计划审阅决策，批准或拒绝以推进执行",
          expectedEffect: "决策闭环，避免延误",
        };
      }
      return { triggered: false };
    },
  },
  {
    id: "kpi_critical",
    name: "KPI 危险区间预警",
    module: "finance",
    priority: "high",
    check: (ctx) => {
      const critical = ctx.kpis.filter((k) => k.status === "critical");
      if (critical.length > 0) {
        return {
          triggered: true,
          problem: `${critical.length} 项 KPI 处于危险区间`,
          evidence: critical
            .slice(0, 3)
            .map((k) => `${k.name}=${k.value}${k.unit || ""}(目标${k.target})`)
            .join("、"),
          action: "立即排查 KPI 偏差原因，制定改善计划",
          expectedEffect: "KPI 回到健康区间",
        };
      }
      return { triggered: false };
    },
  },
  {
    id: "kpi_warning",
    name: "KPI 警告区间提醒",
    module: "finance",
    priority: "medium",
    check: (ctx) => {
      const warning = ctx.kpis.filter((k) => k.status === "warning");
      if (warning.length > 0) {
        return {
          triggered: true,
          problem: `${warning.length} 项 KPI 处于警告区间`,
          evidence: warning
            .slice(0, 3)
            .map((k) => `${k.name}=${k.value}${k.unit || ""}`)
            .join("、"),
          action: "关注 KPI 趋势，预防进一步恶化",
          expectedEffect: "防止 KPI 跌入危险区间",
        };
      }
      return { triggered: false };
    },
  },
  {
    id: "degraded_data",
    name: "数据降级提醒",
    module: "system",
    priority: "low",
    check: (ctx) => {
      if (ctx.ceoData?.degraded) {
        return {
          triggered: true,
          problem: "CEO Dashboard 数据降级，部分 KPI 使用兜底数据",
          evidence: "degraded=true",
          action: "检查 RuntimeManager 是否初始化，各 Runtime 是否正常注册",
          expectedEffect: "恢复真实数据展示",
        };
      }
      return { triggered: false };
    },
  },
  {
    id: "no_brain_report",
    name: "企业总脑未运行",
    module: "system",
    priority: "low",
    check: (ctx) => {
      if (ctx.kpis.length === 0 && ctx.risks.length === 0 && ctx.decisions.length === 0) {
        return {
          triggered: true,
          problem: "EnterpriseBrain 尚未运行，无今日分析数据",
          evidence: "kpis.length=0, risks.length=0, decisions.length=0",
          action: "前往智能晨报点击「生成晨报」触发 EnterpriseBrain 运行",
          expectedEffect: "获取完整企业总脑分析",
        };
      }
      return { triggered: false };
    },
  },
  {
    // V1.5 P0-2/P0-4：高毛利业务加投（Profit Engine 数据）
    id: "sku_margin_high",
    name: "高毛利业务加投建议",
    module: "finance",
    priority: "high",
    check: (ctx) => {
      const profitable = (ctx.skuMargins || []).filter(
        (m) => m.marginRate !== null && m.marginRate >= 0.7 && m.revenue > 0
      );
      if (profitable.length > 0) {
        const names = profitable.slice(0, 3).map((m) => `「${m.productName}」毛利 ${(m.marginRate! * 100).toFixed(0)}%`).join("、");
        return {
          triggered: true,
          problem: `检测到高毛利业务：${names}`,
          evidence: `Profit Engine profit_by_sku：${profitable.map((m) => `${m.productName}=${m.revenue}`).join("、")}`,
          action: "把资源/内容/投放预算向高毛利业务倾斜，扩大成交",
          expectedEffect: `预计提升整体毛利（高毛利业务收入合计 ¥${profitable.reduce((s, m) => s + m.revenue, 0)}）`,
        };
      }
      return { triggered: false };
    },
  },
  {
    // V1.5 P0-2/P0-4：低毛利业务止损（Profit Engine 数据）
    id: "sku_margin_low",
    name: "低毛利业务止损建议",
    module: "finance",
    priority: "high",
    check: (ctx) => {
      const losing = (ctx.skuMargins || []).filter(
        (m) => m.marginRate !== null && m.marginRate <= 0.3 && m.revenue > 0
      );
      if (losing.length > 0) {
        const names = losing.slice(0, 3).map((m) => `「${m.productName}」毛利 ${(m.marginRate! * 100).toFixed(0)}%`).join("、");
        return {
          triggered: true,
          problem: `检测到低毛利业务：${names}`,
          evidence: `Profit Engine profit_by_sku：${losing.map((m) => `${m.productName}=¥${m.revenue}`).join("、")}`,
          action: "停止投入或涨价/改价，避免资源浪费在低毛利业务上",
          expectedEffect: "释放资源转向高毛利业务，改善整体利润率",
        };
      }
      return { triggered: false };
    },
  },
];

const MODULE_CONFIG: Record<SuggestionModule, { label: string; color: string; workspace: string }> = {
  finance: { label: "财务", color: "#10B981", workspace: "finance" },
  crm: { label: "客户", color: "#3B82F6", workspace: "crm" },
  content: { label: "内容", color: "#EC4899", workspace: "marketing" },
  task: { label: "任务", color: "#A855F7", workspace: "ceo" },
  risk: { label: "风险", color: "#EF4444", workspace: "ceo" },
  knowledge: { label: "知识", color: "#06B6D4", workspace: "knowledge" },
  strategy: { label: "战略", color: "#F59E0B", workspace: "ceo" },
  system: { label: "系统", color: "#6B7280", workspace: "ceo" },
};

const PRIORITY_CONFIG: Record<Priority, { label: string; color: string; weight: number }> = {
  high: { label: "高", color: "#EF4444", weight: 3 },
  medium: { label: "中", color: "#F59E0B", weight: 2 },
  low: { label: "低", color: "#3B82F6", weight: 1 },
};

/** V1.5 P0-2：自动执行金额阈值（元）— 涉及金额 <= 该值可自动执行，超过需 CEO 批准 */
const DEFAULT_AUTO_THRESHOLD = 1000;

// ============================================
// 主组件
// ============================================

export default function CEODecisionPanel() {
  const dashboard = useDashboardData();
  const ceoData = dashboard.ceo;
  const setWorkspace = useCockpit4Store((s) => s.setWorkspace);

  const [suggestions, setSuggestions] = useState<DecisionSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [filterPriority, setFilterPriority] = useState<Priority | "all">("all");
  const [filterModule, setFilterModule] = useState<SuggestionModule | "all">("all");
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<DecisionSuggestion[]>([]);

  // ============================================
  // 规则引擎执行
  // ============================================
  const runRules = useCallback(
    async (ctx: RuleContext): Promise<DecisionSuggestion[]> => {
      const now = new Date().toISOString();
      const results: DecisionSuggestion[] = [];

      for (const rule of RULES) {
        try {
          const result = rule.check(ctx);
          if (result.triggered) {
            results.push({
              id: `${rule.id}-${now}`,
              title: rule.name,
              problem: result.problem || "—",
              evidence: result.evidence || "—",
              action: result.action || "—",
              priority: rule.priority,
              module: rule.module,
              source: `规则:${rule.id}`,
              expectedEffect: result.expectedEffect,
              createdAt: now,
            });
          }
        } catch {
          // 单条规则失败不影响其他
        }
      }

      // 补充：从 EnterpriseBrain 决策列表派生建议
      if (ctx.decisions.length > 0 && results.length < 5) {
        const pending = ctx.decisions.filter((d) => d.status === "proposed").slice(0, 2);
        pending.forEach((d) => {
          results.push({
            id: `decision-${d.id || ""}-${now}`,
            title: d.title || "待审决策",
            problem: d.proposal || d.rationale || "—",
            evidence: d.rationale || "—",
            action: "审阅并批准/拒绝该决策",
            priority: "medium",
            module: "strategy",
            source: "EnterpriseBrain",
            expectedEffect: d.expectedROI ? `预期 ROI: ${d.expectedROI}` : undefined,
            createdAt: now,
          });
        });
      }

      // 按优先级排序
      results.sort((a, b) => PRIORITY_CONFIG[b.priority].weight - PRIORITY_CONFIG[a.priority].weight);
      return results;
    },
    []
  );

  // ============================================
  // 生成建议
  // ============================================
  const generateSuggestions = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      // 拉 EnterpriseBrain 数据
      let kpis: any[] = [];
      let risks: any[] = [];
      let decisions: any[] = [];

      try {
        if (RuntimeManager.hasRuntime(RuntimeName.EnterpriseBrain)) {
          // v6.3.25 加 5s 超时：EnterpriseBrain 查询未实现/挂起时避免 loading 永久占位（正文停在 spinner）
          const withTimeout = <T,>(p: Promise<T>, ms = 5000): Promise<T | null> =>
            Promise.race([
              p,
              new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
            ]);
          const [kpiResp, riskResp, decResp] = await Promise.all([
            withTimeout(RuntimeManager.query(RuntimeName.EnterpriseBrain, { type: "get_kpis" })),
            withTimeout(RuntimeManager.query(RuntimeName.EnterpriseBrain, { type: "get_risks" })),
            withTimeout(RuntimeManager.query(RuntimeName.EnterpriseBrain, { type: "get_decisions" })),
          ]);
          if (kpiResp?.success && Array.isArray(kpiResp.data)) kpis = kpiResp.data;
          if (riskResp?.success && Array.isArray(riskResp.data)) risks = riskResp.data;
          if (decResp?.success && Array.isArray(decResp.data)) decisions = decResp.data;
        }
      } catch {
        // 静默降级
      }

      // V1.5 P0-4：拉取产品毛利归因（Profit Engine），供高毛利加投/低毛利止损规则消费
      let skuMargins: RuleContext["skuMargins"] = [];
      try {
        if (RuntimeManager.hasRuntime(RuntimeName.RevenueEngine)) {
          const marginResp = await RuntimeManager.execute(RuntimeName.RevenueEngine, "profit_by_sku", {});
          const m = marginResp?.data as any;
          if (Array.isArray(m?.products)) {
            skuMargins = m.products.map((p: any) => ({
              productName: p.productName,
              revenue: p.revenue,
              marginRate: p.marginRate,
              status: p.status,
              recommendation: p.recommendation,
            }));
          }
        }
      } catch {
        // 毛利数据缺失时规则自动不触发（无假数据）
      }

      // 运行规则引擎
      const newSuggestions = await runRules({ ceoData, kpis, risks, decisions, skuMargins });

      if (newSuggestions.length === 0) {
        // 无触发规则时，生成一条"一切正常"的建议
        newSuggestions.push({
          id: `all-clear-${Date.now()}`,
          title: "企业经营状态健康",
          problem: "所有核心规则未触发预警",
          evidence: `KPI: ${kpis.length} 项，风险: ${risks.length} 项（活跃 ${risks.filter((r) => r.status !== "mitigated").length}），决策: ${decisions.length} 项`,
          action: "继续保持，可专注于战略目标推进和长期规划",
          priority: "low",
          module: "system",
          source: "规则引擎",
          createdAt: new Date().toISOString(),
          expectedEffect: "维持健康状态",
        });
      }

      setSuggestions(newSuggestions);

      // 持久化到 Vault: 11_Strategy/AI建议/{date}.md
      const date = new Date().toISOString().slice(0, 10);
      const content = [
        `---`,
        `type: ceo_decision_suggestions`,
        `date: ${date}`,
        `created_at: ${newSuggestions[0]?.createdAt || new Date().toISOString()}`,
        `count: ${newSuggestions.length}`,
        `---`,
        ``,
        `# 🎯 CEO 决策建议 · ${date}`,
        ``,
        ...newSuggestions.map(
          (s, i) =>
            `## ${i + 1}. ${s.title}\n- **优先级**：${s.priority}（${s.module}）\n- **问题**：${s.problem}\n- **依据**：${s.evidence}\n- **行动**：${s.action}\n- **预期**：${s.expectedEffect || "—"}\n- **来源**：${s.source}\n`
        ),
        ``,
      ].join("\n");

      try {
        await fetch(
          `/vault-api/write?path=${encodeURIComponent(`11_Strategy/AI建议/${date}.md`)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ content }),
          }
        );
      } catch {
        // Vault 写入失败非阻塞
      }

      setMessage({
        type: "ok",
        text: `✅ 已生成 ${newSuggestions.length} 条决策建议\n📄 已写入 Vault：11_Strategy/AI建议/${date}.md`,
      });
    } catch (err) {
      setMessage({
        type: "err",
        text: `❌ 生成失败：${(err as Error).message}`,
      });
    } finally {
      setLoading(false);
    }
  }, [ceoData, runRules]);

  // 挂载时自动生成一次（不消耗 token，仅规则引擎）
  useEffect(() => {
    void generateSuggestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // 一键执行（跳转到对应模块）
  // ============================================
  const executeSuggestion = useCallback(
    (suggestion: DecisionSuggestion) => {
      const targetWorkspace = MODULE_CONFIG[suggestion.module].workspace;
      setWorkspace(targetWorkspace as any);
      // 标记为已执行
      setSuggestions((prev) =>
        prev.map((s) =>
          s.id === suggestion.id
            ? { ...s, executed: true, executedAt: new Date().toISOString() }
            : s
        )
      );
  // 加入历史
      setHistory((prev) => [
        { ...suggestion, executed: true, executedAt: new Date().toISOString() },
        ...prev,
      ].slice(0, 50));
    },
    [setWorkspace]
  );

  // ============================================
  // V1.5 P0-2：批准 / 拒绝决策（金额审批规则）
  //   amount <= autoThreshold → 可自动执行
  //   amount > autoThreshold → 必须 CEO 批准/拒绝
  //   同时联动 Autopilot 的 approve/reject（决策 ID 不存在时静默失败）
  // ============================================
  const decide = useCallback(
    (suggestion: DecisionSuggestion, action: "approved" | "rejected") => {
      const now = new Date().toISOString();
      const decided = { ...suggestion, decision: action, decidedAt: now, executed: action === "approved" };
      setSuggestions((prev) => prev.map((s) => (s.id === suggestion.id ? decided : s)));
      setHistory((prev) => [decided, ...prev].slice(0, 50));
      // 联动 Autopilot 决策审批（无对应决策时静默失败，不阻塞 UI）
      const cmd = action === "approved" ? "approve_decision" : "reject_decision";
      RuntimeManager.execute(RuntimeName.Autopilot, cmd, { id: suggestion.id }).catch(() => {});
      setMessage({
        type: "ok",
        text:
          action === "approved"
            ? `✅ 已批准：${suggestion.title}${suggestion.amount ? `（涉及 ¥${suggestion.amount}）` : ""}`
            : `❌ 已拒绝：${suggestion.title}`,
      });
    },
    []
  );

  /** 判断该建议是否可自动执行（涉及金额 <= 阈值） */
  const canAutoExecute = useCallback((s: DecisionSuggestion) => {
    return (
      typeof s.amount === "number" &&
      typeof s.autoThreshold === "number" &&
      s.amount <= s.autoThreshold
    );
  }, []);

  // ============================================
  // 筛选后的建议
  // ============================================
  const filteredSuggestions = useMemo(() => {
    return suggestions.filter((s) => {
      if (filterPriority !== "all" && s.priority !== filterPriority) return false;
      if (filterModule !== "all" && s.module !== filterModule) return false;
      return true;
    });
  }, [suggestions, filterPriority, filterModule]);

  // 统计
  const stats = useMemo(() => {
    return {
      total: suggestions.length,
      high: suggestions.filter((s) => s.priority === "high").length,
      medium: suggestions.filter((s) => s.priority === "medium").length,
      low: suggestions.filter((s) => s.priority === "low").length,
      executed: suggestions.filter((s) => s.executed).length,
    };
  }, [suggestions]);

  // V1.5 P0-2：待 CEO 决策的建议（未批准也未拒绝）
  const pending = useMemo(() => suggestions.filter((s) => !s.decision), [suggestions]);
  const pendingTop3 = useMemo(
    () =>
      [...pending]
        .sort((a, b) => PRIORITY_CONFIG[b.priority].weight - PRIORITY_CONFIG[a.priority].weight)
        .slice(0, 3),
    [pending]
  );

  // ============================================
  // 渲染
  // ============================================
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-xl border border-white/[0.06] bg-gradient-to-br from-[#131826]/60 to-[#0a0e1a]/60 backdrop-blur-md overflow-hidden"
    >
      {/* 顶部光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-purple-500/40 to-transparent" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/[0.04]">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-md bg-purple-500/15 border border-purple-500/30 flex items-center justify-center">
            <Brain size={13} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-white tracking-tight">CEO 决策建议</h3>
            <p className="text-[9px] font-mono text-gray-500 uppercase tracking-wider mt-0.5">
              AI Decision Panel · {stats.total} 条 · 高 {stats.high} 中 {stats.medium} 低 {stats.low} · 已执行 {stats.executed}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* 历史按钮 */}
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-md border transition-colors ${
              showHistory
                ? "bg-cyan-500/15 border-cyan-500/30 text-cyan-300"
                : "bg-white/[0.03] border-white/[0.06] text-gray-400 hover:text-gray-300"
            }`}
            title="查看历史执行记录"
          >
            <History size={10} />
            <span className="text-[9px] font-mono uppercase tracking-wider">
              历史 {history.length}
            </span>
          </button>
          {/* 生成按钮 */}
          <button
            onClick={generateSuggestions}
            disabled={loading}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 transition-colors disabled:opacity-50"
            title="运行规则引擎 + EnterpriseBrain 数据生成决策建议"
          >
            {loading ? (
              <Loader2 size={11} className="text-purple-400 animate-spin" />
            ) : (
              <RefreshCw size={11} className="text-purple-400" />
            )}
            <span className="text-[9px] font-mono text-purple-300 uppercase tracking-wider">
              {loading ? "生成中..." : "重新生成"}
            </span>
          </button>
        </div>
      </div>

      {/* 消息提示 */}
      {message && (
        <div className="px-4 py-2 border-b border-white/[0.04]">
          <div
            className={`px-3 py-2 rounded-md border text-[10px] font-mono ${
              message.type === "ok"
                ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
                : "border-red-500/30 bg-red-500/[0.06] text-red-300"
            }`}
          >
            <span className="whitespace-pre-line">{message.text}</span>
          </div>
        </div>
      )}

      {/* V1.5 P0-2：今日必须由你决定的 3 件事（决策清单 + 批准/拒绝/进一步分析） */}
      {!showHistory && pendingTop3.length > 0 && (
        <div className="px-4 py-3 border-b border-white/[0.04]">
          <div className="flex items-center gap-2 mb-2">
            <CheckSquare size={11} className="text-cyan-400" />
            <span className="text-[11px] font-semibold text-white">
              今日必须由你决定的 {Math.min(3, pending.length)} 件事
            </span>
            <span className="text-[9px] font-mono text-gray-500">
              金额 ≤{DEFAULT_AUTO_THRESHOLD} 元可自动执行 · 超过需你批准
            </span>
          </div>
          <div className="space-y-2">
            {pendingTop3.map((s) => {
              const prCfg = PRIORITY_CONFIG[s.priority];
              const modCfg = MODULE_CONFIG[s.module];
              const auto = canAutoExecute(s);
              return (
                <motion.div
                  key={s.id}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-md border border-cyan-500/15 bg-cyan-500/[0.03] p-2.5"
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="flex-shrink-0 w-1 self-stretch rounded-full"
                      style={{ background: prCfg.color }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                        <span className="text-[11px] font-semibold text-white">{s.title}</span>
                        <span
                          className="px-1 py-0.5 rounded text-[8px] font-mono uppercase"
                          style={{ background: `${prCfg.color}15`, color: prCfg.color }}
                        >
                          {prCfg.label}优先级
                        </span>
                        <span
                          className="px-1 py-0.5 rounded text-[8px] font-mono uppercase"
                          style={{ background: `${modCfg.color}15`, color: modCfg.color }}
                        >
                          {modCfg.label}
                        </span>
                        {s.amount !== undefined && (
                          <span
                            className={`px-1 py-0.5 rounded text-[8px] font-mono uppercase ${
                              auto
                                ? "bg-emerald-500/15 text-emerald-300"
                                : "bg-amber-500/15 text-amber-300"
                            }`}
                          >
                            ¥{s.amount.toLocaleString("zh-CN")} · {auto ? "可自动执行" : "需批准"}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-400 leading-snug mb-1.5">
                        {s.problem}
                        {s.expectedEffect ? (
                          <span className="text-emerald-400"> — {s.expectedEffect}</span>
                        ) : null}
                      </div>
                      {/* 操作按钮：批准 / 拒绝 / 进一步分析 */}
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => decide(s, "approved")}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors text-[9px] font-mono uppercase tracking-wider"
                        >
                          <CheckCircle2 size={10} />
                          批准
                        </button>
                        <button
                          onClick={() => decide(s, "rejected")}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors text-[9px] font-mono uppercase tracking-wider"
                        >
                          <X size={10} />
                          拒绝
                        </button>
                        <button
                          onClick={() =>
                            setSuggestions((prev) =>
                              prev.map((x) =>
                                x.id === s.id ? { ...x, expanded: !x.expanded } : x
                              )
                            )
                          }
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.1] text-gray-300 hover:bg-white/[0.08] transition-colors text-[9px] font-mono uppercase tracking-wider"
                          title="展开完整依据"
                        >
                          <ChevronRight size={10} className={s.expanded ? "rotate-90 transition-transform" : "transition-transform"} />
                          进一步分析
                        </button>
                      </div>
                      {s.expanded && (
                        <div className="mt-2 space-y-1 text-[9px] font-mono text-gray-500">
                          <div>依据：{s.evidence}</div>
                          <div>建议动作：<span style={{ color: prCfg.color }}>{s.action}</span></div>
                          <div>来源：{s.source}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* 筛选条 */}
      {suggestions.length > 0 && !showHistory && (
        <div className="px-4 py-2 border-b border-white/[0.04] flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 text-[9px] font-mono text-gray-600 uppercase tracking-wider">
            <Filter size={10} />
            筛选：
          </div>
          {/* 优先级筛选 */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setFilterPriority("all")}
              className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${
                filterPriority === "all"
                  ? "bg-white/10 text-white"
                  : "text-gray-500 hover:text-gray-300"
              }`}
            >
              全部
            </button>
            {(["high", "medium", "low"] as Priority[]).map((p) => (
              <button
                key={p}
                onClick={() => setFilterPriority(p)}
                className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${
                  filterPriority === p
                    ? "text-white"
                    : "text-gray-500 hover:text-gray-300"
                }`}
                style={
                  filterPriority === p
                    ? { background: `${PRIORITY_CONFIG[p].color}20`, color: PRIORITY_CONFIG[p].color }
                    : undefined
                }
              >
                {PRIORITY_CONFIG[p].label}
              </button>
            ))}
          </div>
          {/* 模块筛选 */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setFilterModule("all")}
              className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${
                filterModule === "all"
                  ? "bg-white/10 text-white"
                  : "text-gray-500 hover:text-gray-300"
              }`}
            >
              全部模块
            </button>
            {(Object.keys(MODULE_CONFIG) as SuggestionModule[]).map((m) => (
              <button
                key={m}
                onClick={() => setFilterModule(m)}
                className={`px-1.5 py-0.5 rounded text-[9px] font-mono uppercase ${
                  filterModule === m
                    ? "text-white"
                    : "text-gray-500 hover:text-gray-300"
                }`}
                style={
                  filterModule === m
                    ? { background: `${MODULE_CONFIG[m].color}20`, color: MODULE_CONFIG[m].color }
                    : undefined
                }
              >
                {MODULE_CONFIG[m].label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 内容区 */}
      <div className="p-4 max-h-[480px] overflow-y-auto">
        {showHistory ? (
          /* 历史记录视图 */
          <div>
            {history.length === 0 ? (
              <p className="text-center py-8 text-[11px] font-mono text-gray-500">
                暂无历史执行记录
              </p>
            ) : (
              <div className="space-y-1.5">
                {history.map((h, i) => {
                  const modCfg = MODULE_CONFIG[h.module];
                  return (
                    <div
                      key={i}
                      className="flex items-center gap-2 p-2 rounded-md border border-white/[0.04] bg-white/[0.02]"
                    >
                      <CheckCircle2 size={12} className="text-emerald-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] text-gray-200 truncate">{h.title}</div>
                        <div className="text-[9px] font-mono text-gray-600">
                          {h.executedAt ? new Date(h.executedAt).toLocaleString("zh-CN", { hour12: false }) : ""} · {modCfg.label}
                        </div>
                      </div>
                      <ChevronRight size={10} className="text-gray-600 flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="py-10 text-center">
            <Loader2 size={20} className="mx-auto text-purple-400 animate-spin mb-2" />
            <p className="text-[11px] font-mono text-gray-500">
              运行规则引擎 + 聚合 EnterpriseBrain 数据...
            </p>
          </div>
        ) : filteredSuggestions.length === 0 ? (
          <div className="py-10 text-center">
            <Sparkles size={20} className="mx-auto text-gray-700 mb-2" />
            <p className="text-[11px] font-mono text-gray-500">
              暂无符合筛选条件的建议
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <AnimatePresence mode="popLayout">
              {filteredSuggestions.map((s, idx) => {
                const prCfg = PRIORITY_CONFIG[s.priority];
                const modCfg = MODULE_CONFIG[s.module];
                return (
                  <motion.div
                    key={s.id}
                    layout
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 8 }}
                    transition={{ delay: idx * 0.03 }}
                    className={`rounded-md border p-3 ${
                      s.executed
                        ? "border-emerald-500/20 bg-emerald-500/[0.03] opacity-60"
                        : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
                    } transition-all`}
                  >
                    <div className="flex items-start gap-2.5">
                      {/* 优先级标签 */}
                      <div
                        className="flex-shrink-0 w-1 self-stretch rounded-full"
                        style={{ background: prCfg.color }}
                      />

                      {/* 主内容 */}
                      <div className="flex-1 min-w-0">
                        {/* 标题行 */}
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <span className="text-[12px] font-semibold text-white">
                            {s.title}
                          </span>
                          <span
                            className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase"
                            style={{ background: `${prCfg.color}15`, color: prCfg.color }}
                          >
                            {prCfg.label}优先级
                          </span>
                          <span
                            className="px-1.5 py-0.5 rounded text-[8px] font-mono uppercase"
                            style={{ background: `${modCfg.color}15`, color: modCfg.color }}
                          >
                            {modCfg.label}
                          </span>
                          {s.executed && (
                            <span className="flex items-center gap-0.5 text-[8px] font-mono text-emerald-400 uppercase">
                              <CheckCircle2 size={9} />
                              已执行
                            </span>
                          )}
                        </div>

                        {/* 详情 */}
                        <div className="space-y-1">
                          <div className="text-[10px] leading-snug">
                            <span className="font-mono text-gray-600">问题：</span>
                            <span className="text-gray-300">{s.problem}</span>
                          </div>
                          <div className="text-[10px] leading-snug">
                            <span className="font-mono text-gray-600">依据：</span>
                            <span className="text-gray-400 font-mono">{s.evidence}</span>
                          </div>
                          <div className="text-[10px] leading-snug">
                            <span className="font-mono text-gray-600">建议：</span>
                            <span style={{ color: prCfg.color }}>{s.action}</span>
                          </div>
                          {s.expectedEffect && (
                            <div className="text-[10px] leading-snug">
                              <span className="font-mono text-gray-600">预期：</span>
                              <span className="text-emerald-400">{s.expectedEffect}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-[9px] font-mono text-gray-600">
                            <Clock size={9} />
                            {new Date(s.createdAt).toLocaleTimeString("zh-CN", { hour12: false })}
                            <span>·</span>
                            <span>{s.source}</span>
                          </div>
                        </div>
                      </div>

                      {/* 一键执行按钮 */}
                      {!s.executed && (
                        <button
                          onClick={() => executeSuggestion(s)}
                          className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/30 hover:bg-cyan-500/25 transition-colors"
                          title={`跳转到${modCfg.label}模块处理`}
                        >
                          <Zap size={10} className="text-cyan-400" />
                          <span className="text-[9px] font-mono text-cyan-300 uppercase tracking-wider">
                            执行
                          </span>
                        </button>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </motion.div>
  );
}
